import React, { useCallback, useEffect, useState } from "react";
import { Select } from "antd";
import { getUsers } from "../../../../apis/user";
import { saveVisit, updateVisit, sendIPDAdmissionWhatsApp } from "../../../../apis/visit";
import { useNotification } from "../../../../NotificationContext";
import { format } from "date-fns";
import { getRooms } from "../../../../apis/room";
import MultiSelect from "../../../../components/MultiSelect";
import { getDisorders, saveDisorder } from "../../../../apis/disorder";
import { getInsuranceProviders } from "../../../../apis/insuranceProviders";
import { Option } from "../../../../interfaces/MultiSelectTypes";
import whatsAppIcon from "../../../../assets/images/icons/whatsapp.svg";

const { Option: SelectOption } = Select;

interface IPDAdmissionFormProps {
  patientId: number;
  clinicVisitTypeId: number;
  initialData?: {
    admittingDoctorId?: number | null;
    chiefComplaint?: string;
    provisionalDiagnosis?: any[];
    roomId?: number | null;
    bedId?: number | null;
    paymentType?: "self" | "insurance" | "corporate";
    insuranceProviderId?: number | null;
    policyNumber?: string;
    coverageType?: "reimbursement" | "cashless";
    admissionDate?: string;
    admissionTime?: string;
  };
  visitId?: number;
  admissionEntryId?: number;
  onSave: (visit: any) => void;
  onCancel: () => void;
}

const IPDAdmissionForm: React.FC<IPDAdmissionFormProps> = ({
  patientId,
  clinicVisitTypeId,
  initialData,
  visitId,
  admissionEntryId,
  onSave,
  onCancel,
}) => {
  const { showNotification } = useNotification();
  const [doctors, setDoctors] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [sendWhatsAppOnAdmit, setSendWhatsAppOnAdmit] = useState(true);
  const [disorders, setDisorders] = useState<Array<Option>>([]);
  const [insuranceProviders, setInsuranceProviders] = useState<Array<{ id: number; name: string }>>([]);

  // Generate time slots in 15-minute intervals in AM/PM format
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time24 = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
        const time12 = convertTo12Hour(time24);
        slots.push({ value: time24, label: time12 });
      }
    }
    return slots;
  };

  // Convert 24-hour format (HH:mm) to 12-hour AM/PM format
  const convertTo12Hour = (time24: string): string => {
    const [hours, minutes] = time24.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
  };


  // Round time to nearest 15-minute slot
  const roundToNearest15Minutes = (time: string): string => {
    const [hours, minutes] = time.split(":").map(Number);
    const roundedMinutes = Math.round(minutes / 15) * 15;
    if (roundedMinutes >= 60) {
      return `${(hours + 1).toString().padStart(2, "0")}:00`;
    }
    return `${hours.toString().padStart(2, "0")}:${roundedMinutes.toString().padStart(2, "0")}`;
  };

  const timeSlots = generateTimeSlots();
  const currentTime = format(new Date(), "HH:mm");
  const defaultTime = roundToNearest15Minutes(currentTime);

  // Normalize any date string to yyyy-MM-dd for HTML date input
  const normalizeToDateString = (value: string | undefined): string => {
    if (!value || typeof value !== "string") return format(new Date(), "yyyy-MM-dd");
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return format(new Date(), "yyyy-MM-dd");
    return format(parsed, "yyyy-MM-dd");
  };

  const buildFormData = useCallback(
    (data?: IPDAdmissionFormProps["initialData"] | any) => {
      console.log("IPDAdmissionForm buildFormData input:", data);
      const rawDate =
        data?.admissionDate ??
        (data as any)?.admission_date ??
        "";
      return {
        admittingDoctorId: data?.admittingDoctorId || (null as number | null),
        chiefComplaint: data?.chiefComplaint || "",
        provisionalDiagnosis: (data?.provisionalDiagnosis || []).map((d: any) => ({
          label:
            d.label ||
            d.name ||
            d.disorder?.name ||
            d.disorder_name ||
            d.disorderName ||
            String(d.value || d.id || d.disorder_id || d.disorderId || ""),
          value: String(d.value || d.id || d.disorder_id || d.disorderId || ""),
        })) as Option[],
        roomId: data?.roomId || (null as number | null),
        bedId: data?.bedId ?? (null as number | null),
        paymentType: data?.paymentType || ("self" as "self" | "insurance" | "corporate"),
        insuranceProviderId: data?.insuranceProviderId ?? null,
        policyNumber: data?.policyNumber ?? "",
        coverageType: data?.coverageType ?? (data as any)?.coverage_type ?? "reimbursement",
        admissionDate: normalizeToDateString(rawDate || undefined),
        admissionTime: data?.admissionTime ?? (data as any)?.admission_time ?? defaultTime, // Store in 24-hour format internally
      };
    },
    [defaultTime]
  );

  const [formData, setFormData] = useState(buildFormData(initialData));

  useEffect(() => {
    setFormData(buildFormData(initialData));
  }, [buildFormData, initialData]);

  useEffect(() => {
    fetchDoctors();
    fetchDisorders();
    fetchInsuranceProviders();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res: any = await getUsers({ role: "doctor", per_page: 1000 });
      setDoctors(res.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch doctors:", error);
    }
  };

  const fetchRooms = useCallback(async () => {
    try {
      // When editing: fetch all rooms (so current room shows even if now occupied).
      // When creating: fetch only rooms with at least one free bed.
      const params: any = { per_page: 1000 };
      if (!visitId) params.has_available_beds = 1;
      const res: any = await getRooms(params);
      const list = res?.data?.data ?? res?.data ?? res ?? [];
      setRooms(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    }
  }, [visitId]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  const selectedRoom = rooms.find((r: any) => r.id === formData.roomId);

  // For create: all rooms fetched already have available beds (filtered server-side)
  const isEditMode = !!visitId;
  const availableRooms = rooms;
  const roomBedsForSelect = selectedRoom?.beds ?? [];
  const availableBeds = isEditMode
    ? roomBedsForSelect
    : roomBedsForSelect.filter(
        (b: any) =>
          String(b.status || "").toLowerCase() === "available" ||
          b.is_available === true
      );
  const mustSelectBed = availableBeds.length > 1;
  const selectedBed = roomBedsForSelect.find((b: any) => b.id === formData.bedId);

  // Keep bed in sync with selected room (create mode): auto-select first available bed when needed
  useEffect(() => {
    if (isEditMode || !formData.roomId) return;
    const room = rooms.find((r: any) => r.id === formData.roomId);
    const beds = room?.beds ?? [];
    const availBeds = beds.filter((b: any) => b.status === "available" || b.is_available === true);
    if (!availBeds.length) return;

    const currentBedValid = !!availBeds.find((b: any) => b.id === formData.bedId);
    if (!currentBedValid) {
      const fallbackBed = availBeds[0];
      setFormData((prev: any) => ({ ...prev, bedId: fallbackBed?.id ?? null }));
    }
  }, [formData.roomId, formData.bedId, rooms, isEditMode]);

  const fetchDisorders = async (search: string = "") => {
    try {
      const {
        data: { data: response },
      } = await getDisorders({ name: search, type: "disease" });
      console.log({ response });

      const disorderData = response.map((i: any) => {
        return {
          label: i.name,
          value: String(i.id),
        };
      });
      setDisorders(disorderData);
    } catch (error) {
      console.error("Failed to fetch disorders:", error);
    }
  };

  const fetchInsuranceProviders = async () => {
    try {
      const res: any = await getInsuranceProviders({ per_page: 1000 });
      const list = res?.data?.data ?? res?.data ?? res ?? [];
      setInsuranceProviders(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Failed to fetch insurance providers:", error);
    }
  };

  const handleDiagnosisSearch = (search: string) => {
    fetchDisorders(search);
  };

  const handleDiagnosisSelect = (option: Option) => {
    if (
      !formData.provisionalDiagnosis.some(
        (selected) => selected.value === option.value
      )
    ) {
      setFormData({
        ...formData,
        provisionalDiagnosis: [...formData.provisionalDiagnosis, option],
      });
    }
  };

  const handleDiagnosisRemove = (option: Option) => {
    const updatedDiagnosis = formData.provisionalDiagnosis.filter(
      (selected) => selected.value !== option.value
    );
    setFormData({
      ...formData,
      provisionalDiagnosis: updatedDiagnosis,
    });
  };

  const createNewDisorder = async (disorderName: string) => {
    try {
      const { data } = await saveDisorder({ name: disorderName, type: "disease" });
      setFormData({
        ...formData,
        provisionalDiagnosis: [
          ...formData.provisionalDiagnosis,
          {
            label: data.data.name,
            value: String(data.data.id),
          },
        ],
      });
      fetchDisorders();
    } catch (error) {
      console.error("Failed to create disorder:", error);
    }
  };

  const handleSave = async () => {
    const validationErrors: any = {};

    if (!formData.admittingDoctorId) {
      validationErrors.admittingDoctorId = "Admitting Doctor is required";
    }
    if (!formData.chiefComplaint.trim()) {
      validationErrors.chiefComplaint = "Chief Complaint is required";
    }
    if (!formData.roomId) {
      validationErrors.roomId = "Room is required";
    }
    if (mustSelectBed && !formData.bedId) {
      validationErrors.bedId = "Please select a bed for this room";
    }
    if (!formData.admissionDate) {
      validationErrors.admissionDate = "Admission Date is required";
    }
    if (!formData.admissionTime) {
      validationErrors.admissionTime = "Admission Time is required";
    }
    if (formData.paymentType === "insurance") {
      if (!formData.insuranceProviderId) {
        validationErrors.insuranceProviderId = "Insurance provider is required";
      }
      if (!formData.policyNumber?.trim()) {
        validationErrors.policyNumber = "Policy number is required";
      }
      if (!formData.coverageType) {
        validationErrors.coverageType = "Coverage type is required";
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSaving(true);

    try {
      // Combine date and time
      const admissionDateTime = new Date(
        `${formData.admissionDate}T${formData.admissionTime}`
      ).toISOString();

      // Filter out invalid values and convert to objects with disorder_id
      const validDiagnosis = formData.provisionalDiagnosis
        .filter((d: Option) => d && d.value && !isNaN(Number(d.value)))
        .map((d: Option) => ({ disorder_id: Number(d.value) }));

      const visitPayload = {
        patientId,
        clinicVisitTypeId,
        userId: formData.admittingDoctorId,
        chiefComplaint: formData.chiefComplaint,
        provisionalDiagnosis: validDiagnosis,
        date: admissionDateTime,
        isInpatient: true,
        is_scheduled: false,
        roomId: formData.roomId,
        bedId: formData.bedId ?? null,
        meta: {
          roomId: formData.roomId,
          bedId: formData.bedId ?? null,
          paymentType: formData.paymentType,
          ...(formData.paymentType === "insurance" && {
            insuranceProviderId: formData.insuranceProviderId,
            policyNumber: formData.policyNumber?.trim() || undefined,
            coverageType: formData.coverageType || undefined,
          }),
          admissionDate: formData.admissionDate,
          admissionTime: formData.admissionTime,
        },
      };

      let savedVisit: any;

      if (visitId) {
        // Update existing visit
        savedVisit = await updateVisit({
          id: visitId,
          ...visitPayload,
        });
        showNotification(
          "success",
          "Admission updated successfully",
          "",
          "topRight"
        );
      } else {
        // Create new visit
        savedVisit = await saveVisit(visitPayload);
        // Admission entry creation is handled by backend, not called from frontend
        // The backend should automatically create the admission entry when the visit is created
        showNotification(
          "success",
          "Patient admitted successfully",
          "",
          "topRight"
        );
        // Send admission WhatsApp to patient if option is checked
        if (sendWhatsAppOnAdmit && savedVisit?.id) {
          try {
            await sendIPDAdmissionWhatsApp(savedVisit.id);
            showNotification(
              "success",
              "Admission WhatsApp sent to patient",
              "",
              "topRight"
            );
          } catch (waError: any) {
            showNotification(
              "warning",
              "Admission saved but WhatsApp could not be sent",
              waError?.response?.data?.message || "",
              "topRight"
            );
          }
        }
      }

      onSave(savedVisit);
    } catch (error: any) {
      console.error("Failed to save admission:", error);
      const message =
        error?.response?.data?.message || "Failed to save admission";
      showNotification("error", message, "", "topRight");
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Admitting Doctor <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select Admitting Doctor"
          value={formData.admittingDoctorId}
          onChange={(value) =>
            setFormData({ ...formData, admittingDoctorId: value })
          }
        >
          {doctors.map((doctor) => (
            <SelectOption key={doctor.id} value={doctor.id}>
              {doctor.firstName} {doctor.lastName}
            </SelectOption>
          ))}
        </Select>
        {errors.admittingDoctorId && (
          <span className="text-sm text-red-500">{errors.admittingDoctorId}</span>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Chief Complaint <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.chiefComplaint}
          onChange={(e) =>
            setFormData({ ...formData, chiefComplaint: e.target.value })
          }
          rows={3}
          className="px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          placeholder="Enter chief complaint..."
        />
        {errors.chiefComplaint && (
          <span className="text-sm text-red-500">{errors.chiefComplaint}</span>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Provisional Diagnosis
        </label>
        <MultiSelect
          onAddNewOption={createNewDisorder}
          selectedOptions={formData.provisionalDiagnosis}
          options={disorders}
          onSearch={handleDiagnosisSearch}
          onOptionSelect={handleDiagnosisSelect}
          onOptionRemove={handleDiagnosisRemove}
          placeholder="Select or search disorders..."
        />
      </div>

      {isEditMode ? (
        <>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Room
            </label>
            <div className="px-3 py-2 w-full rounded-md border border-gray-200 bg-gray-50 text-gray-800">
              {selectedRoom
                ? `${selectedRoom.name}${selectedRoom.roomType?.name ? ` (${selectedRoom.roomType.name})` : ""}`
                : formData.roomId
                  ? `Room #${formData.roomId}`
                  : "—"}
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Bed
            </label>
            <div className="px-3 py-2 w-full rounded-md border border-gray-200 bg-gray-50 text-gray-800">
              {selectedBed
                ? `${selectedBed.name}${selectedBed.type ? ` — ${selectedBed.type}` : ""}`
                : formData.bedId
                  ? `Bed #${formData.bedId}`
                  : "—"}
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Room <span className="text-red-500">*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Room"
              value={formData.roomId}
              onChange={(value) => {
                const room = rooms.find((r: any) => r.id === value);
                const beds = room?.beds ?? [];
                const firstAvailableBed = beds.find((b: any) => b.status === "available" || b.is_available === true);
                const autoBed = firstAvailableBed ?? beds[0] ?? null;
                setFormData({ ...formData, roomId: value, bedId: autoBed?.id ?? null });
              }}
            >
              {availableRooms.map((room: any) => (
                <SelectOption key={room.id} value={room.id}>
                  {room.name} {room.roomType?.name ? `(${room.roomType.name})` : ""}
                </SelectOption>
              ))}
            </Select>
            {availableRooms.length === 0 && (
              <span className="text-sm text-amber-600">No available rooms. All rooms are occupied or under maintenance.</span>
            )}
            {errors.roomId && (
              <span className="text-sm text-red-500">{errors.roomId}</span>
            )}
          </div>

          {mustSelectBed && (
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Bed <span className="text-red-500">*</span>
              </label>
              <Select
                style={{ width: "100%" }}
                placeholder="Select Bed"
                value={formData.bedId}
                onChange={(value) => setFormData({ ...formData, bedId: value })}
              >
                {availableBeds.map((bed: any) => (
                  <SelectOption key={bed.id} value={bed.id}>
                    {bed.name}
                    {bed.type ? ` — ${bed.type}` : ""}
                  </SelectOption>
                ))}
              </Select>
              {errors.bedId && (
                <span className="text-sm text-red-500">{errors.bedId}</span>
              )}
            </div>
          )}

          {selectedRoom && availableBeds.length === 1 && (
            <div className="text-sm text-gray-600">
              Bed: <span className="font-medium">{availableBeds[0].name}</span>
              {availableBeds[0].type ? ` (${availableBeds[0].type})` : ""}
            </div>
          )}
        </>
      )}

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Payment Type <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          value={formData.paymentType}
          onChange={(value: "self" | "insurance" | "corporate") =>
            setFormData({
              ...formData,
              paymentType: value,
              ...(value === "insurance"
                ? { coverageType: formData.coverageType || "reimbursement" }
                : { insuranceProviderId: null, policyNumber: "", coverageType: undefined }),
            })
          }
        >
          <SelectOption value="self">Self</SelectOption>
          <SelectOption value="insurance">Insurance</SelectOption>
          <SelectOption value="corporate">Corporate</SelectOption>
        </Select>
      </div>

      {formData.paymentType === "insurance" && (
        <>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Insurance Provider <span className="text-red-500">*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select insurance provider"
              value={formData.insuranceProviderId}
              onChange={(value) =>
                setFormData({ ...formData, insuranceProviderId: value })
              }
            >
              {insuranceProviders.map((provider) => (
                <SelectOption key={provider.id} value={provider.id}>
                  {provider.name}
                </SelectOption>
              ))}
            </Select>
            {errors.insuranceProviderId && (
              <span className="text-sm text-red-500">{errors.insuranceProviderId}</span>
            )}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Policy Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.policyNumber}
              onChange={(e) =>
                setFormData({ ...formData, policyNumber: e.target.value })
              }
              className="px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Enter policy number"
            />
            {errors.policyNumber && (
              <span className="text-sm text-red-500">{errors.policyNumber}</span>
            )}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Coverage Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-6 mt-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  value="reimbursement"
                  checked={formData.coverageType === "reimbursement"}
                  onChange={(e) =>
                    setFormData({ ...formData, coverageType: "reimbursement" })
                  }
                  className="text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Reimbursement</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="coverageType"
                  value="cashless"
                  checked={formData.coverageType === "cashless"}
                  onChange={(e) =>
                    setFormData({ ...formData, coverageType: "cashless" })
                  }
                  className="text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Cashless</span>
              </label>
            </div>
            {errors.coverageType && (
              <span className="text-sm text-red-500">{errors.coverageType}</span>
            )}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Admission Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.admissionDate}
            onChange={(e) =>
              setFormData({ ...formData, admissionDate: e.target.value })
            }
            className="px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
          {errors.admissionDate && (
            <span className="text-sm text-red-500">{errors.admissionDate}</span>
          )}
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Admission Time <span className="text-red-500">*</span>
          </label>
          <Select
            style={{ width: "100%" }}
            placeholder="Select Admission Time"
            value={formData.admissionTime}
            onChange={(value) =>
              setFormData({ ...formData, admissionTime: value })
            }
            showSearch
            filterOption={(input, option) => {
              const children = String(option?.children || "");
              return children.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {timeSlots.map((slot) => (
              <SelectOption key={slot.value} value={slot.value}>
                {slot.label}
              </SelectOption>
            ))}
          </Select>
          {errors.admissionTime && (
            <span className="text-sm text-red-500">{errors.admissionTime}</span>
          )}
        </div>
      </div>

      {!visitId && (
        <div className="flex gap-2 items-center">
          <input
            type="checkbox"
            id="send-whatsapp-admit"
            checked={sendWhatsAppOnAdmit}
            onChange={(e) => setSendWhatsAppOnAdmit(e.target.checked)}
            className="text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
          />
          <label htmlFor="send-whatsapp-admit" className="text-sm text-gray-700">
            Send WhatsApp notification to patient after admission
          </label>
        </div>
      )}

      <div className="flex flex-wrap gap-3 justify-end pt-4">
        {visitId && (
          <button
            type="button"
            onClick={async () => {
              if (!visitId) return;
              setIsSendingWhatsApp(true);
              try {
                await sendIPDAdmissionWhatsApp(visitId);
                showNotification(
                  "success",
                  "Admission WhatsApp sent to patient",
                  "",
                  "topRight"
                );
              } catch (error: any) {
                showNotification(
                  "error",
                  "Failed to send WhatsApp",
                  error?.response?.data?.message || "",
                  "topRight"
                );
              } finally {
                setIsSendingWhatsApp(false);
              }
            }}
            disabled={isSendingWhatsApp || isSaving}
            className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          >
            <img alt="whatsapp" src={whatsAppIcon} className="w-5 h-5" />
            {isSendingWhatsApp ? "Sending..." : "Send WhatsApp"}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? "Saving..." : visitId ? "Update Admission" : "Admit Patient"}
        </button>
      </div>
    </div>
  );
};

export default IPDAdmissionForm;

